// 男性吸引力报告生成器
export interface MaleAttractivenessData {
  age: number;
  gender: string;
  overallAttractiveness: {
    score: number;
    rating: string;
  };
  masculinityIndex: {
    score: number;
    level: string;
  };
  facialSymmetry: {
    score: number;
    level: string;
  };
  jawlineStrength: {
    score: number;
    strength: string;
  };
  skinQuality: {
    score: number;
    level: string;
  };
  smileAttractiveness: {
    score: number;
    appeal: string;
  };
  ageAppropriateness: {
    score: number;
    category: string;
  };
}

export interface AttractivenessReport {
  summary: {
    title: string;
    description: string;
    highlights: string[];
  };
  detailedAnalysis: {
    category: string;
    score: number;
    feedback: string;
    impact: string;
    tips: string[];
  }[];
  personalizedAdvice: {
    category: string;
    priority: 'High' | 'Medium' | 'Low';
    suggestion: string;
    actionItems: string[];
  }[];
  attractivenessType: {
    type: string;
    description: string;
    strengths: string[];
    characteristics: string[];
  };
}

export class MaleAttractivenessReportGenerator {
  
  static generateReport(data: MaleAttractivenessData): AttractivenessReport {
    const summary = this.generateSummary(data);
    const detailedAnalysis = this.generateDetailedAnalysis(data);
    const personalizedAdvice = this.generatePersonalizedAdvice(data);
    const attractivenessType = this.determineAttractivenessType(data);

    return {
      summary,
      detailedAnalysis,
      personalizedAdvice,
      attractivenessType
    };
  }

  private static generateSummary(data: MaleAttractivenessData) {
    const score = data.overallAttractiveness.score;
    const rating = data.overallAttractiveness.rating;
    
    const summaryTemplates = {
      'Exceptional': {
        title: 'Outstanding Male Attractiveness',
        description: `With an exceptional score of ${score}/10, you possess remarkable masculine appeal. Your facial features demonstrate excellent harmony and strong masculine characteristics that create a powerful and attractive presence.`,
        highlights: [
          'Exceptional overall attractiveness rating',
          'Strong masculine facial features',
          'Excellent facial harmony and proportion'
        ]
      },
      'High': {
        title: 'High Male Attractiveness',
        description: `Your attractiveness score of ${score}/10 indicates very strong masculine appeal. You have attractive features with good facial structure and appealing masculine characteristics.`,
        highlights: [
          'High attractiveness rating',
          'Strong masculine appeal',
          'Well-balanced facial features'
        ]
      },
      'Good': {
        title: 'Good Male Attractiveness',
        description: `With a solid score of ${score}/10, you demonstrate good masculine appeal and attractive features. You have a strong foundation with excellent potential for enhancement.`,
        highlights: [
          'Good overall attractiveness',
          'Solid masculine features',
          'Strong potential for improvement'
        ]
      },
      'Average': {
        title: 'Average Male Attractiveness',
        description: `Your score of ${score}/10 shows average masculine appeal with balanced features. There are several areas where focused improvements can significantly enhance your attractiveness.`,
        highlights: [
          'Balanced facial features',
          'Good foundation to build upon',
          'Multiple enhancement opportunities'
        ]
      },
      'Below Average': {
        title: 'Developing Male Attractiveness',
        description: `Your current score of ${score}/10 indicates developing masculine appeal. With targeted improvements in key areas, you can significantly enhance your overall attractiveness.`,
        highlights: [
          'Clear improvement opportunities',
          'Potential for significant enhancement',
          'Focused development areas identified'
        ]
      }
    };

    const template = summaryTemplates[rating as keyof typeof summaryTemplates];
    
    // 添加年龄相关的亮点
    if (data.ageAppropriateness.category === 'Prime Appeal') {
      template.highlights.push('Optimal age for masculine appeal');
    } else if (data.ageAppropriateness.category === 'Distinguished Appeal') {
      template.highlights.push('Mature and distinguished presence');
    }

    return template;
  }

  private static generateDetailedAnalysis(data: MaleAttractivenessData) {
    return [
      {
        category: 'Overall Attractiveness',
        score: data.overallAttractiveness.score,
        feedback: this.getOverallFeedback(data.overallAttractiveness),
        impact: 'Primary factor in masculine appeal',
        tips: this.getOverallTips(data.overallAttractiveness.score)
      },
      {
        category: 'Masculinity Index',
        score: data.masculinityIndex.score,
        feedback: this.getMasculinityFeedback(data.masculinityIndex),
        impact: 'Defines masculine presence and appeal',
        tips: this.getMasculinityTips(data.masculinityIndex.level)
      },
      {
        category: 'Jawline Strength',
        score: data.jawlineStrength.score,
        feedback: this.getJawlineFeedback(data.jawlineStrength),
        impact: 'Key masculine facial feature',
        tips: this.getJawlineTips(data.jawlineStrength.strength)
      },
      {
        category: 'Facial Symmetry',
        score: data.facialSymmetry.score,
        feedback: this.getSymmetryFeedback(data.facialSymmetry),
        impact: 'Foundation of facial attractiveness',
        tips: this.getSymmetryTips(data.facialSymmetry.level)
      },
      {
        category: 'Skin Quality',
        score: data.skinQuality.score,
        feedback: this.getSkinFeedback(data.skinQuality),
        impact: 'Overall appearance and health indicator',
        tips: this.getSkinTips(data.skinQuality.level)
      },
      {
        category: 'Smile Attractiveness',
        score: data.smileAttractiveness.score,
        feedback: this.getSmileFeedback(data.smileAttractiveness),
        impact: 'Social appeal and approachability',
        tips: this.getSmileTips(data.smileAttractiveness.appeal)
      }
    ];
  }

  private static generatePersonalizedAdvice(data: MaleAttractivenessData) {
    const advice = [];

    // 基于皮肤质量的建议
    if (data.skinQuality.score < 6) {
      advice.push({
        category: 'Skincare',
        priority: 'High' as const,
        suggestion: 'Establish a comprehensive daily skincare routine to improve skin health and appearance.',
        actionItems: [
          'Use a gentle cleanser twice daily',
          'Apply moisturizer with SPF protection',
          'Consider professional skincare consultation',
          'Maintain proper hydration and nutrition'
        ]
      });
    }

    // 基于下颌线强度的建议
    if (data.jawlineStrength.strength === 'Weak' || data.jawlineStrength.strength === 'Moderate') {
      advice.push({
        category: 'Facial Structure',
        priority: 'Medium' as const,
        suggestion: 'Enhance jawline definition through targeted techniques and styling.',
        actionItems: [
          'Practice jaw exercises and facial muscle training',
          'Consider strategic facial hair styling',
          'Maintain good posture to showcase jawline',
          'Explore contouring techniques for photos'
        ]
      });
    }

    // 基于微笑吸引力的建议
    if (data.smileAttractiveness.score < 7) {
      advice.push({
        category: 'Expression & Confidence',
        priority: 'Medium' as const,
        suggestion: 'Improve smile attractiveness and overall expression confidence.',
        actionItems: [
          'Practice genuine, natural smiles',
          'Consider dental care and hygiene optimization',
          'Work on confident body language',
          'Practice positive facial expressions'
        ]
      });
    }

    // 基于年龄的建议
    if (data.age > 35) {
      advice.push({
        category: 'Mature Appeal',
        priority: 'Low' as const,
        suggestion: 'Embrace and enhance mature masculine charm and sophistication.',
        actionItems: [
          'Focus on sophisticated grooming and style',
          'Emphasize confidence and experience',
          'Maintain physical fitness and health',
          'Develop distinguished personal style'
        ]
      });
    }

    // 基于整体分数的建议
    if (data.overallAttractiveness.score < 6) {
      advice.push({
        category: 'Overall Enhancement',
        priority: 'High' as const,
        suggestion: 'Focus on comprehensive attractiveness improvement across multiple areas.',
        actionItems: [
          'Develop consistent grooming routine',
          'Improve physical fitness and posture',
          'Enhance personal style and wardrobe',
          'Build confidence through self-improvement'
        ]
      });
    }

    return advice;
  }

  private static determineAttractivenessType(data: MaleAttractivenessData) {
    const { overallAttractiveness, masculinityIndex, jawlineStrength, smileAttractiveness } = data;

    // Alpha Male Type
    if (jawlineStrength.strength === 'Very Strong' && masculinityIndex.score > 8) {
      return {
        type: 'Alpha Male',
        description: 'Strong, dominant masculine appeal with commanding presence and powerful facial structure.',
        strengths: ['Powerful jawline', 'High masculinity index', 'Commanding presence'],
        characteristics: ['Natural leadership appeal', 'Strong facial structure', 'Confident masculine energy']
      };
    }

    // Charming Gentleman Type
    if (smileAttractiveness.score > 7.5 && data.skinQuality.score > 7) {
      return {
        type: 'Charming Gentleman',
        description: 'Approachable masculine charm with warm, inviting features and excellent social appeal.',
        strengths: ['Engaging smile', 'Good skin quality', 'Approachable demeanor'],
        characteristics: ['Warm and inviting', 'Social magnetism', 'Refined attractiveness']
      };
    }

    // Classic Handsome Type
    if (data.facialSymmetry.score > 8 && overallAttractiveness.score > 7) {
      return {
        type: 'Classic Handsome',
        description: 'Timeless masculine beauty with excellent facial harmony and balanced features.',
        strengths: ['Excellent facial symmetry', 'Balanced features', 'Timeless appeal'],
        characteristics: ['Classic masculine beauty', 'Harmonious features', 'Universal appeal']
      };
    }

    // Mature Distinguished Type
    if (data.age > 40 && data.ageAppropriateness.score > 7) {
      return {
        type: 'Distinguished Gentleman',
        description: 'Mature masculine sophistication with refined appeal and experienced charm.',
        strengths: ['Mature sophistication', 'Refined presence', 'Experienced charm'],
        characteristics: ['Distinguished appearance', 'Sophisticated appeal', 'Mature confidence']
      };
    }

    // Default: Developing Appeal Type
    return {
      type: 'Developing Appeal',
      description: 'Emerging masculine attractiveness with strong potential for enhancement and growth.',
      strengths: ['Growth potential', 'Improvement opportunities', 'Developing features'],
      characteristics: ['Room for enhancement', 'Potential for growth', 'Developing masculine appeal']
    };
  }

  // Helper methods for generating specific feedback
  private static getOverallFeedback(overall: { score: number; rating: string }) {
    const feedbackMap = {
      'Exceptional': 'Your overall attractiveness is truly outstanding, placing you in the top tier of masculine appeal.',
      'High': 'You possess very attractive masculine features that create strong appeal and positive impressions.',
      'Good': 'Your attractiveness shows solid masculine appeal with good potential for further enhancement.',
      'Average': 'You have balanced features with several opportunities to enhance your masculine appeal.',
      'Below Average': 'There are clear opportunities to improve your overall masculine attractiveness through targeted enhancements.'
    };
    return feedbackMap[overall.rating as keyof typeof feedbackMap];
  }

  private static getOverallTips(score: number): string[] {
    if (score >= 8.5) {
      return ['Maintain your excellent grooming standards', 'Focus on confidence and presence', 'Consider professional photography'];
    } else if (score >= 7.0) {
      return ['Enhance your personal style', 'Improve grooming consistency', 'Work on confident body language'];
    } else if (score >= 5.5) {
      return ['Develop a comprehensive grooming routine', 'Focus on fitness and health', 'Improve wardrobe and style'];
    } else {
      return ['Start with basic grooming improvements', 'Focus on health and fitness', 'Seek professional style guidance'];
    }
  }

  private static getMasculinityFeedback(masculinity: { score: number; level: string }) {
    const feedbackMap = {
      'Very High': 'Your masculinity index is exceptionally strong, conveying powerful masculine presence.',
      'High': 'You demonstrate strong masculine characteristics that enhance your overall appeal.',
      'Moderate': 'Your masculine features are well-balanced with room for enhancement.',
      'Low': 'There are opportunities to strengthen your masculine presence and appeal.'
    };
    return feedbackMap[masculinity.level as keyof typeof feedbackMap];
  }

  private static getMasculinityTips(level: string): string[] {
    const tipsMap = {
      'Very High': ['Maintain your strong masculine presence', 'Focus on confident leadership qualities'],
      'High': ['Continue developing masculine confidence', 'Enhance your natural masculine traits'],
      'Moderate': ['Work on building masculine confidence', 'Focus on posture and presence'],
      'Low': ['Develop masculine body language', 'Focus on confidence building exercises']
    };
    return tipsMap[level as keyof typeof tipsMap] || [];
  }

  private static getJawlineFeedback(jawline: { score: number; strength: string }) {
    const feedbackMap = {
      'Very Strong': 'Your jawline is exceptionally well-defined, significantly contributing to your masculine appeal.',
      'Strong': 'You have a well-defined jawline that enhances your masculine features effectively.',
      'Moderate': 'Your jawline has good definition with potential for further enhancement.',
      'Weak': 'Your jawline could benefit from enhancement techniques to strengthen masculine appeal.'
    };
    return feedbackMap[jawline.strength as keyof typeof feedbackMap];
  }

  private static getJawlineTips(strength: string): string[] {
    const tipsMap = {
      'Very Strong': ['Maintain good posture to showcase your jawline', 'Consider professional photography angles'],
      'Strong': ['Practice jaw exercises to maintain definition', 'Use good lighting in photos'],
      'Moderate': ['Try facial exercises for jaw muscle tone', 'Consider strategic facial hair styling'],
      'Weak': ['Practice jaw strengthening exercises', 'Explore contouring techniques', 'Consider facial hair to define jawline']
    };
    return tipsMap[strength as keyof typeof tipsMap] || [];
  }

  private static getSymmetryFeedback(symmetry: { score: number; level: string }) {
    const feedbackMap = {
      'Excellent': 'Your facial symmetry is outstanding, creating excellent overall facial harmony.',
      'Good': 'You have good facial symmetry that contributes positively to your attractiveness.',
      'Fair': 'Your facial symmetry is adequate with some minor asymmetries.',
      'Poor': 'There are noticeable asymmetries that could be addressed through various techniques.'
    };
    return feedbackMap[symmetry.level as keyof typeof feedbackMap];
  }

  private static getSymmetryTips(level: string): string[] {
    const tipsMap = {
      'Excellent': ['Maintain your excellent facial balance', 'Use symmetrical poses in photos'],
      'Good': ['Practice good facial posture', 'Be mindful of photo angles'],
      'Fair': ['Practice facial exercises', 'Consider professional photo techniques'],
      'Poor': ['Work with professional photographers', 'Consider facial exercises', 'Focus on good angles']
    };
    return tipsMap[level as keyof typeof tipsMap] || [];
  }

  private static getSkinFeedback(skin: { score: number; level: string }) {
    const feedbackMap = {
      'Excellent': 'Your skin quality is outstanding, contributing significantly to your overall appeal.',
      'Good': 'You have good skin quality that enhances your attractiveness.',
      'Fair': 'Your skin quality is adequate with room for improvement.',
      'Poor': 'Your skin quality could benefit from focused care and attention.'
    };
    return feedbackMap[skin.level as keyof typeof feedbackMap];
  }

  private static getSkinTips(level: string): string[] {
    const tipsMap = {
      'Excellent': ['Maintain your excellent skincare routine', 'Continue using SPF protection'],
      'Good': ['Enhance your current skincare routine', 'Consider professional treatments'],
      'Fair': ['Establish a consistent skincare routine', 'Focus on hydration and protection'],
      'Poor': ['Start with basic cleansing and moisturizing', 'Consider professional skincare consultation']
    };
    return tipsMap[level as keyof typeof tipsMap] || [];
  }

  private static getSmileFeedback(smile: { score: number; appeal: string }) {
    const feedbackMap = {
      'Very Attractive': 'Your smile is very attractive and significantly enhances your appeal.',
      'Attractive': 'You have an attractive smile that contributes positively to your charm.',
      'Moderate': 'Your smile has good potential with room for enhancement.',
      'Needs Improvement': 'Your smile could benefit from focused improvement techniques.'
    };
    return feedbackMap[smile.appeal as keyof typeof feedbackMap];
  }

  private static getSmileTips(appeal: string): string[] {
    const tipsMap = {
      'Very Attractive': ['Maintain your excellent smile', 'Use your smile as a key asset'],
      'Attractive': ['Continue practicing genuine smiles', 'Focus on dental hygiene'],
      'Moderate': ['Practice natural, genuine smiles', 'Consider dental care improvements'],
      'Needs Improvement': ['Work on smile confidence', 'Consider professional dental consultation']
    };
    return tipsMap[appeal as keyof typeof tipsMap] || [];
  }
}
